import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

const STORAGE_PREFIX = "ui_url_state:";

// 같은 키라도 화면(경로)마다 저장 공간을 분리해 값이 섞이지 않게 한다.
function buildStorageKey(key: string): string {
  const path = typeof window !== "undefined" ? window.location.pathname : "";
  return `${STORAGE_PREFIX}${path}:${key}`;
}

function readStorage(storageKey: string): string | null {
  try {
    return sessionStorage.getItem(storageKey);
  } catch {
    return null;
  }
}

function writeStorage(storageKey: string, value: string): void {
  try {
    sessionStorage.setItem(storageKey, value);
  } catch {
    // 저장 실패(용량 초과 등)는 무시
  }
}

/**
 * URL 쿼리 파라미터와 sessionStorage를 함께 사용해 화면 상태를 유지하는 훅.
 *
 * - 상태가 바뀌면 주소창(URL)의 쿼리 파라미터를 갱신 → 새로고침·링크 공유에도 유지
 * - URL에 값이 없으면 sessionStorage에서 복원 (탭 복귀/리다이렉트 대응)
 * - 기본값으로 되돌아가면 URL에서 파라미터를 제거해 주소를 깔끔하게 유지
 */
export default function useUrlState(
  key: string,
  defaultValue: string
): [string, (value: string) => void] {
  const [searchParams, setSearchParams] = useSearchParams();
  const storageKey = buildStorageKey(key);

  const paramValue = searchParams.get(key);

  const [value, setValue] = useState<string>(() => {
    if (paramValue !== null) return paramValue;
    return readStorage(storageKey) ?? defaultValue;
  });

  // 최초 마운트 시, URL에 없더라도 복원된 값이 기본값과 다르면 주소에 반영
  const mountedRef = useRef(false);
  useEffect(() => {
    if (mountedRef.current) return;
    mountedRef.current = true;
    if (searchParams.get(key) === null && value !== defaultValue) {
      setSearchParams(
        (prev) => {
          const sp = new URLSearchParams(prev);
          sp.set(key, value);
          return sp;
        },
        { replace: true }
      );
    }
    // 최초 1회만 실행
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 뒤로/앞으로 가기 등으로 URL이 바뀌면 상태를 동기화
  useEffect(() => {
    const next = paramValue ?? readStorage(storageKey) ?? defaultValue;
    setValue((prev) => (prev === next ? prev : next));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramValue]);

  const update = useCallback(
    (next: string) => {
      // 입력 즉시 상태를 반영해 타이핑 지연/커서 튐을 방지
      setValue(next);
      writeStorage(storageKey, next);
      setSearchParams(
        (prev) => {
          const sp = new URLSearchParams(prev);
          if (next === defaultValue) {
            sp.delete(key);
          } else {
            sp.set(key, next);
          }
          return sp;
        },
        { replace: true }
      );
    },
    [key, defaultValue, setSearchParams, storageKey]
  );

  return [value, update];
}